"use client";

import { cn } from "@/lib/utils";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import MonarchResolver from "@/monarch/MonarchResolver";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import type { AbandonedCartIntentMatchTestResult } from "@/monarch/rules/abandonedCartIntentMatch/types";
import { AbandonedCartSuggestionCard } from "./AbandonedCartSuggestionCard";
import useAbandonedCartIntent from "@/hooks/useAbandonedCartIntent";

interface AbandonedCartSuggestionsProps {
  className?: string;
  maxSuggestions?: number;
  pageLocation?: string;
}

interface AbandonedCartSuggestionsComponentProps
  extends AbandonedCartSuggestionsProps {
  monarchData: AbandonedCartIntentMatchTestResult;
}

/**
 * AbandonedCartSuggestions component displays suggested products based on user's product view history.
 * Shows products the user has viewed but not purchased, regardless of current cart contents.
 */
function AbandonedCartSuggestionsComponent({
  className,
  maxSuggestions = 1,
  monarchData,
  pageLocation = "page",
}: AbandonedCartSuggestionsComponentProps) {
  const { cardProps, isLoading, error } =
    useAbandonedCartIntent(maxSuggestions);

  if (isLoading) {
    return null;
  }

  if (error) {
    return null;
  }

  if (!cardProps || cardProps.length === 0) {
    return null;
  }

  // Filter out cards with missing required fields
  const validCards = cardProps.filter(
    (props) =>
      props.productId &&
      props.productId.trim() !== "" &&
      props.productUrl &&
      props.productUrl.trim() !== "" &&
      props.productName &&
      props.productName.trim() !== "" &&
      props.checkoutUrl &&
      props.checkoutUrl.trim() !== "" &&
      typeof props.displayPrice === "number",
  );

  if (validCards.length === 0) {
    return null;
  }

  // Get text fields from Monarch (static props)
  const ctaText = monarchData.data?.productCardCtaText || "Add to cart";
  const headerTextTemplate = monarchData.data?.bannerHeaderText;
  const subheaderText = monarchData.data?.bannerSubheaderText;

  return (
    <div
      className={cn("flex justify-center", className)}
      role="region"
      aria-label="Suggested products"
    >
      {validCards.map((props) => {
        // Process header text with subject already included
        const processedHeaderText = headerTextTemplate
          ? headerTextTemplate.replace("{category}", props.subject || "")
          : props.subject
            ? `Still interested in ${props.subject}?`
            : "Still interested?";

        return (
          <AbandonedCartSuggestionCard
            key={props.productId}
            {...props}
            ctaText={ctaText}
            headerText={processedHeaderText}
            subheaderText={subheaderText}
            pageLocation={pageLocation}
          />
        );
      })}
    </div>
  );
}

/**
 * AbandonedCartSuggestions wrapper with feature flag and Monarch rule gating
 */
export default function AbandonedCartSuggestions(
  props: AbandonedCartSuggestionsProps,
) {
  return (
    <FeatureFlagComponentResolverClient
      flagKey="NEXT_PUBLIC_FEATURE_PRODUCT_VIEW_TRACKING"
      variants={{
        legacy: null,
        test: (
          <MonarchResolver<AbandonedCartIntentMatchTestResult>
            experimentKey="useAbandonedCartIntentMatch"
            variants={{
              control: null,
              test: (
                <MonarchDataConsumer<AbandonedCartIntentMatchTestResult>
                  experimentKey="useAbandonedCartIntentMatch"
                  render={(testResult) => (
                    <AbandonedCartSuggestionsComponent
                      {...props}
                      monarchData={testResult}
                    />
                  )}
                />
              ),
            }}
            Loader={null}
            fallbackVariant="control"
          />
        ),
      }}
      fallback="legacy"
    />
  );
}
